class ReportWidgetResultVM extends BaseResultVM {
    result: Array<any>;
    resultTotal: (any | null)[] | null;
    resultSubTotals: (any | null)[][] | null;
    columns: Array<GridColumn>;
    hasGrouping: boolean;
    currencyCode: string;
    reportType: ReportTypeEnum;
    formattingRules: Array<FormattingRuleVM>;
    groupColumns: Array<number>;
    dateFormat: string;
    numberFixedColumns: number;
    itemConfig: ReportConfig;
    widgetType: WidgetType;

    filters: Filter[];
    drilldownFilters: Filter[];
    drilldownId: number;

    //Frontend properties
    baseColumns: Array<any>;
    gridColumns: Array<any>;
    grid: DevExpress.ui.dxDataGrid;
    pivotGrid: DevExpress.ui.dxPivotGrid;
    gridContainer: any;
    formulasResolved: boolean;
    totalItems: Array<any>;
    groupItems: Array<any>;
    resizeColumnsCfg: ResizeColumnsCfg;
    collapseRows: Array<any>;
    searchBox: DevExpress.ui.dxTextBox;

    constructor(data) {
        super(data);

        this.result = data.result;
        this.resultTotal = data.resultTotal;
        this.resultSubTotals = data.resultSubTotals;
        this.columns = createInstances(GridColumn, data.columns);
        this.hasGrouping = data.hasGrouping;
        this.currencyCode = data.currencyCode;
        this.reportType = data.reportType;
        this.formattingRules = createInstances(FormattingRuleVM, data.formattingRules);
        this.groupColumns = data.groupColumns;
        this.dateFormat = data.dateFormat;
        this.numberFixedColumns = data.numberFixedColumns;
        this.itemConfig = data.itemConfig;
        this.widgetType = data.widgetType;
        this.filters = createInstances(Filter, data.filters ?? []);
        this.drilldownFilters = data.drilldownFilters;
        this.drilldownId = data.drilldownId;
    }
}

class GridColumn {
    dataField?: string;
    caption?: string;
    isMetric?: boolean;
    format?: ColumnValueFormat;
    dataType?: string;
    thresholdValue?: number | null;
    thresholdColor?: string;
    hasFormattingRule?: boolean;
    formattingRuleId?: number;
    isGrouping?: boolean;
    groupingType?: GroupingTypeEnum;
    queryName?: string;
    alignment?: string;
    titleAlignment?: string;
    fixed?: boolean;
    fixedPosition?: string;
    backgroundColor?: string;
    foreColor?: string;
    hasBarChart?: boolean;
    visualizationMode?: VisualizationModeDefinition;
    sortIndex?: number | null;
    sortOrder?: string;
    index?: number;
    indexBottom?: number;
    autoGrouping?: AutoGroupingEnum;
    comparisonType?: ComparisonMetricType;
    comparisonFilterId?: string;
    indexBottomComparison?: number;
    indexComparison?: number;
    groupName?: string;
    groupOrder?: number | null;
    formulaTop?: string;
    visible?: boolean;
    visibleIndex?: number;
    visibleField?: boolean;
    groupId?: any;
    functionType?: FunctionTypeEnum;
    showSubtotalValue?: boolean;
    showTotalValue?: boolean;
    accoutingStyle?: boolean;
    width?: string | number;
    calculateTotals?: CalculateTotalsEnum;

    //DevExpress Properties
    name?: string;
    groupIndex?: any;
    headerCellTemplate?: any;
    customizeText?: any;
    cellTemplate?: any;
    groupCellTemplate?: any;
    calculateCellValue?: any;
    calculateDisplayValue?: any;
    sortingMethod?: any;
    area?: any;
    encodeHtml?: boolean;
    summaryType?: any;

    //Frontend Properties
    allowExpandAll?: boolean;
    expanded?: boolean;
    dependentColumns?: Array<GridColumn>;
    calculateSortValue?: string;
    cssClass?: string;
    indexBase?: number;

    //GroupColumns
    groupColumns?: Array<GridColumn>;

    constructor(data) {
        this.dataField = data.dataField;
        this.caption = data.caption;
        this.isMetric = data.isMetric;
        this.format = data.format;
        this.dataType = data.dataType;
        this.thresholdValue = data.thresholdValue;
        this.thresholdColor = data.thresholdColor;
        this.hasFormattingRule = data.hasFormattingRule;
        this.formattingRuleId = data.formattingRuleId;
        this.isGrouping = data.isGrouping;
        this.groupingType = data.groupingType;
        this.queryName = data.queryName;
        this.alignment = data.alignment;
        this.titleAlignment = data.titleAlignment;
        this.fixed = data.fixed;
        this.fixedPosition = "sticky";
        this.backgroundColor = data.backgroundColor;
        this.foreColor = data.foreColor;
        this.hasBarChart = data.hasBarChart;
        this.visualizationMode = new VisualizationModeDefinition(data.visualizationMode);
        this.sortIndex = data.sortIndex;
        this.sortOrder = data.sortOrder;
        this.index = data.index;
        this.indexBottom = data.indexBottom;
        this.autoGrouping = data.autoGrouping;
        this.comparisonType = data.comparisonType;
        this.comparisonFilterId = data.comparisonFilterId;
        this.indexBottomComparison = data.indexBottomComparison;
        this.indexComparison = data.indexComparison;
        this.groupName = data.groupName;
        this.groupOrder = data.groupOrder;
        this.formulaTop = data.formulaTop;
        this.visible = data.visible;
        this.visibleIndex = data.visibleIndex;
        this.visibleField = data.visibleField;
        this.groupId = data.groupId;
        this.functionType = data.functionType;
        this.showSubtotalValue = data.showSubtotalValue;
        this.showTotalValue = data.showTotalValue;
        this.accoutingStyle = data.accoutingStyle;
        this.width = data.width;
        this.calculateTotals = data.calculateTotals;
    }

    toString(value: any, values: Array<any>, isTotal: boolean, isSubTotal: boolean, isSubTotalStart: boolean, result: ReportWidgetResultVM, reportConfig: ReportConfig) {
        let valueText = value;
        if (this.isMetric) {
            if (this.groupingType === GroupingTypeEnum.WSUM) {
                value = values[this.indexBottom] === 0 ? 0 : values[this.index] / values[this.indexBottom];
            }

            var dx = DevExpress as any;
            if (this.format.style === "percent") {
                valueText = `${dx.formatHelper.format(value, { style: "percent", precision: this.format.precision, currencySign: this.format.currencySign })}`;
            }
            else if (this.format.style === "currency") {
                valueText = `${dx.formatHelper.format(value, { style: `currency`, currency: this.format.currency, currencyDisplay: `narrowSymbol`, precision: this.format.precision, currencySign: this.format.currencySign })}`;
            }
            else {
                valueText = `${dx.formatHelper.format(value, { style: "decimal", precision: this.format.precision, currencySign: this.format.currencySign })}`;
            }
        }
        else {
            if (isTotal) {
                valueText = reportConfig.grandTotalText();
                if (reportConfig.showReportRowCount()) {
                    valueText += ` (${result.result.filter(x => x[0] === "Detail").length})`;
                }
            }
            else if (isSubTotal || isSubTotalStart) {
                let fields = result.columns.filter(x => x.isGrouping && x.groupId === 0);
                valueText = "";
                for (let field of fields) {
                    if (valueText) {
                        valueText += `${irSettings.columnSeparatorForHtml}`;
                    }

                    let groupByValue = values[field.index];
                    if (field.autoGrouping !== AutoGroupingEnum.None) {
                        groupByValue = getAutoGroupingText(groupByValue, field.autoGrouping);
                    }

                    valueText += groupByValue;
                }

                if (isSubTotal) {
                    if (reportConfig.showReportRowCount()) {
                        let groupByFields = fields.filter(x => x.groupingType === GroupingTypeEnum.GROUP_BY);
                        let subTotalCount = result.result.filter(x => x[0] == IrUtils.detailRowTypeName && groupByFields.every(g => x[g.index] == values[g.index])).length;
                        valueText += ` (${subTotalCount})`;
                    }

                    if (reportConfig.subtotalsPosition() === TotalsPositionEnum.Bottom) {
                        valueText = `${reportConfig.subTotalText()} ${valueText}`;
                    }
                }
            }
            else {
                if (this.autoGrouping !== AutoGroupingEnum.None) {
                    valueText = getAutoGroupingText(value, this.autoGrouping);
                }
            }
        }

        return valueText;
    }

    getCellStyle(value: any, values: Array<any>, reportConfig: ReportConfig) {
        let styles = {
            color: this.foreColor,
            textAlign: this.alignment,
            fontSize: reportConfig.fontSize()
        } as any;

        if (this.backgroundColor && this.backgroundColor.toLowerCase() !== "#ffffff") {
            styles.backgroundColor = this.backgroundColor;
        }

        if (this.isMetric) {
            if (values && this.groupingType === GroupingTypeEnum.WSUM) {
                value = values[this.indexBottom] === 0 ? 0 : values[this.index] / values[this.indexBottom];
            }

            if (this.thresholdColor && this.thresholdValue !== null && this.thresholdValue !== undefined) {
                styles.color = value < this.thresholdValue ? this.thresholdColor : styles.color;
            }
        }

        if (reportConfig.columnsWidthType() !== ColumnsWidthTypeEnum.Automatic) {
            styles.width = this.width;
        }

        return styles;
    }

    getColumnHeaderStyle(reportConfig: ReportConfig) {
        let styles = {
            //backgroundColor: this.backgroundColor,
            //color: this.foreColor,
            textAlign: this.titleAlignment,
            fontSize: reportConfig.fontSize()
        } as any;

        if (reportConfig.columnsWidthType() !== ColumnsWidthTypeEnum.Automatic) {
            styles.width = this.width;
        }

        return styles;
    }

    getColumnHeaderAttr(reportConfig: ReportConfig) {
        let styles = {} as any;

        if (reportConfig.columnsWidthType() !== ColumnsWidthTypeEnum.Automatic) {
            styles.width = this.width;
        }

        return styles;
    }
}

interface ColumnValueFormat {
    style: string;
    currency: string;
    currencyDisplay: string;
    type: string;
    precision: number;
    minimumFractionDigits: number;
    maximumFractionDigits: number;
    currencySign: string;
    useGrouping: boolean;
}

interface ResizeColumnsCfg {
    inProgress: boolean;
    column: any;
    width: any;
    optionName: string;
}